// Sugalarm — Cycle 309: the server-authoritative glucose utterance
// pipeline (context → veracity gate → humanized sentence).
//
// Every spoken glucose sentence in the SPA is composed HERE, from a
// context resolved AT SPEAK TIME — never from a value captured when a
// timer was armed or an alert fired. The two consumers are the
// auto-announce cadence (App.getAnnouncementText) and the kiosk voice
// query ("what's my glucose", useVoiceDispatch); both were previously
// `statusSpeechText`, which derived the spoken zone CLIENT-SIDE from
// `zoneOf(sgv, local thresholds)` — including the DEFAULT_THRESHOLDS
// loading fallback — and could therefore announce "running high" against
// the server's truth. This module never re-derives a zone: the spoken
// classification is the reading's server-stamped `classification` field
// (attached by the alert engine's own mapping), and a reading without
// one is spoken NEUTRALLY (value + trend, no zone claim).
//
// The veracity gate (cycle-309 contract, applied in order):
//   1. provenance  — synthetic readings (dev-inject / demo feed) never
//                    speak through the real announcement path.
//   2. recovery    — an utterance queued for an alert whose row is
//                    server-recovered speaks the recovery sentence, never
//                    the original alert sentence.
//   3. staleness   — a reading older than the platform's established
//                    stale threshold (`isStale`, provider-aware) is never
//                    spoken as current: the stale variant or the no-data
//                    variant, or silence for the unprompted cadence.
//   4. consistency — the tripwire: if the template's implied
//                    classification disagrees with the context's, the
//                    utterance ABORTS and a `tts_veracity_abort` event is
//                    reported. This is the check that would have caught
//                    the reported "speaks high when it is not" bug.
// Every rewrite/abort/suppression reports a bounded telemetry event via
// `reportVeracityEvent` (never the text, never a name).
//
// Copy contract (cycle 309, operator-approved): member name first, then
// value, then trend, then guidance if urgent. Trend words are exactly
// `rising fast | rising | steady | falling | falling fast`. Values are
// spoken as plain numbers. No em dashes in any spoken copy. Urgent Low
// stays unmistakably alarming.
//
// CYCLE 387 — VALUES ARE SPOKEN AS WORDS, not digits ("the member's glucose is
// one hundred twelve and steady"). The live TTS engine mis-reads a bare
// numeral, and the value is the whole point of the sentence. `spellValue`
// is the single seam — never interpolate a raw number into spoken copy.
// The written surfaces (UI, SMS, push) keep their numerals on purpose.

import { reportVeracityEvent } from "../api/tts";
import { ageMinutes, isStale, trendInfo } from "./glucose";
import { spellValue } from "./spellNumber";
import type { Alert, Reading } from "./types";

export type UtteranceClassification =
  | "in_range"
  | "low"
  | "urgent_low"
  | "high"
  | "urgent_high";

export type UtteranceAlertState = "none" | "active" | "recovered";

/** The single server-authoritative context every spoken glucose sentence
 *  is composed from. Resolve immediately before synthesis — never hold
 *  one across a timer or queue. */
export interface GlucoseUtteranceContext {
  member_id: string | null;
  member_name: string;
  value_mgdl: number | null;
  /** Contract trend word ("" when the reading carries no trend). */
  trend: string;
  /** Server-stamped zone; null = unknown (pre-309 payload) → spoken
   *  neutrally, never re-derived locally. */
  classification: UtteranceClassification | null;
  reading_age_seconds: number | null;
  alert_state: UtteranceAlertState;
  provenance: "real" | "synthetic";
  stale: boolean;
}

/** Cycle 330.3 — units, SPOKEN. A TTS engine reads "mg/dL" as garbage
 *  ("em-gee-dee-ell", or worse), so any sentence that speaks units must
 *  spell them out. This is the single mapping — do not inline a second one.
 *
 *  NOTE: the standard glucose announcements (`templateFor` below) speak the
 *  bare number and NO units, by the cycle-309 operator-approved copy
 *  contract ("the member's glucose is 112 and steady."). This helper exists for the
 *  sentences that DO say them — currently the Settings → Voice Speak sample. */
export function spokenUnits(units: "mg/dL" | "mmol/L"): string {
  return units === "mmol/L"
    ? "millimoles per liter"
    : "milligrams per deciliter";
}

const CLASSIFICATIONS: readonly string[] = [
  "in_range",
  "low",
  "urgent_low",
  "high",
  "urgent_high",
];

/** Map the existing trend enum (via trendInfo().short) onto the five
 *  contract words. "rising slow"/"falling slow" fold into their plain
 *  forms; unknown/absent trends become "" (the trend clause is omitted). */
export function contractTrendWord(
  trend: Reading["trend"],
  trendRate: number | null,
): string {
  // No trend data at all → no trend claim (trendInfo would default a
  // null trend + null rate to "steady", which the veracity contract
  // treats as an unfounded claim — omit the clause instead).
  if (trend == null && trendRate == null) return "";
  const short = trendInfo(trend ?? null, trendRate ?? null).short;
  switch (short) {
    case "rising fast":
      return "rising fast";
    case "rising":
    case "rising slow":
      return "rising";
    case "steady":
      return "steady";
    case "falling":
    case "falling slow":
      return "falling";
    case "falling fast":
      return "falling fast";
    default:
      return "";
  }
}

export interface ResolveArgs {
  reading: Reading | null;
  memberId: string | null;
  memberName: string | null | undefined;
  /** The server-authored alert row mirror (socket.lastAlert) — its
   *  `recovered_at` is the ONLY recovery truth the gate consults. */
  lastAlert: Alert | null;
  now?: number;
}

/** Build the utterance context from the CURRENT app state. Pure. */
export function resolveUtteranceContext(args: ResolveArgs): GlucoseUtteranceContext {
  const { reading, memberId, memberName, lastAlert } = args;
  const now = args.now ?? Date.now();
  const rawClass = reading?.classification ?? null;
  const classification = CLASSIFICATIONS.includes(rawClass ?? "")
    ? (rawClass as UtteranceClassification)
    : null;
  // The alert row only counts when it belongs to the SAME member the
  // sentence names (end-to-end member_id match — contract).
  const alertForMember =
    lastAlert != null && memberId != null && lastAlert.member_id === memberId
      ? lastAlert
      : null;
  const alert_state: UtteranceAlertState =
    alertForMember == null
      ? "none"
      : alertForMember.recovered_at != null
        ? "recovered"
        : "active";
  return {
    member_id: memberId,
    member_name: (memberName ?? "").trim(),
    value_mgdl: reading?.sgv ?? null,
    trend: reading ? contractTrendWord(reading.trend, reading.trend_rate) : "",
    classification,
    reading_age_seconds: reading
      ? Math.max(0, Math.round(ageMinutes(reading.timestamp, now) * 60))
      : null,
    alert_state,
    provenance: reading?.provenance === "synthetic" ? "synthetic" : "real",
    stale: reading
      ? isStale(reading.timestamp, now, reading.provider)
      : true,
  };
}

// ─── Humanized templates (copy contract) ────────────────────────────────

function withTrend(base: string, trend: string): string {
  return trend ? `${base} and ${trend}.` : `${base}.`;
}

/** The sentence for a classification, plus the classification the template
 *  ITSELF implies — the consistency tripwire compares the two. */
function templateFor(
  ctx: GlucoseUtteranceContext,
): { text: string; implied: UtteranceClassification | null } {
  const name = ctx.member_name || "Glucose";
  const named = ctx.member_name.length > 0;
  // Spoken, therefore spelled. `templateFor` is only ever reached with a
  // non-null value (the gate returns the no-data sentence first), but the
  // null-guard keeps the template honest if that order ever changes.
  const v = ctx.value_mgdl == null ? "" : spellValue(ctx.value_mgdl);
  const subject = named ? `${ctx.member_name}'s glucose` : "Glucose";
  switch (ctx.classification) {
    case "in_range":
      return { text: withTrend(`${subject} is ${v}`, ctx.trend), implied: "in_range" };
    case "high":
      return { text: withTrend(`${subject} is high at ${v}`, ctx.trend), implied: "high" };
    case "urgent_high":
      return {
        text: `Attention. ${withTrend(`${subject} is very high at ${v}`, ctx.trend)}`,
        implied: "urgent_high",
      };
    case "low":
      return { text: withTrend(`${subject} is low at ${v}`, ctx.trend), implied: "low" };
    case "urgent_low":
      return {
        text: `Urgent. ${subject} is dangerously low at ${v}. ${
          named ? `Please check on ${name} now.` : "Please check now."
        }`,
        implied: "urgent_low",
      };
    default:
      // Unknown classification (pre-309 payload): NEUTRAL — value + trend,
      // no zone claim. Implied is null so the tripwire has nothing to
      // contradict.
      return { text: withTrend(`${subject} is ${v}`, ctx.trend), implied: null };
  }
}

function recoverySentence(ctx: GlucoseUtteranceContext): string {
  const subject = ctx.member_name ? `${ctx.member_name}'s glucose` : "Glucose";
  return ctx.value_mgdl != null && !ctx.stale
    ? `${subject} is back in range at ${spellValue(ctx.value_mgdl)}.`
    : `${subject} is back in range.`;
}

function staleSentence(ctx: GlucoseUtteranceContext): string {
  const subject = ctx.member_name
    ? `${ctx.member_name}'s last reading`
    : "The last reading";
  const mins = Math.max(
    1,
    Math.round((ctx.reading_age_seconds ?? 0) / 60),
  );
  const value = ctx.value_mgdl == null ? "" : spellValue(ctx.value_mgdl);
  return `${subject} was ${value}, about ${spellValue(mins)} ${
    mins === 1 ? "minute" : "minutes"
  } ago. Waiting for a new reading.`;
}

function noDataSentence(ctx: GlucoseUtteranceContext): string {
  return ctx.member_name
    ? `No recent reading for ${ctx.member_name}.`
    : "No recent reading.";
}

// ─── The gate + composer ────────────────────────────────────────────────

export interface ComposeOpts {
  /** Telemetry surface tag ("auto_announce" | "voice_query" | …). */
  surface: string;
  /** How to treat a stale reading: the unprompted cadence stays SILENT
   *  (cycle-235.3 rule); an explicit user query SPEAKS the stale variant. */
  staleMode: "silent" | "speak";
  /** True when this utterance was queued for/because of an alert — the
   *  recovery rewrite only applies to those (rule 2). */
  queuedForAlert?: boolean;
}

export interface ComposeResult {
  /** null = stay silent (suppressed or aborted). */
  text: string | null;
  /** Why the gate rewrote/suppressed, for callers/tests. */
  gate:
    | "spoken"
    | "suppress_synthetic"
    | "rewrite_recovery"
    | "rewrite_stale"
    | "silent_stale"
    | "silent_no_data"
    | "abort_inconsistent";
}

/** Gate + compose a glucose utterance from an AT-SPEAK-TIME context.
 *  Ordered per the cycle-309 contract; every rewrite/abort reports a
 *  bounded telemetry event. */
export function composeGlucoseUtterance(
  ctx: GlucoseUtteranceContext,
  opts: ComposeOpts,
): ComposeResult {
  // 1. Provenance — synthetic never speaks on the real path.
  if (ctx.provenance === "synthetic") {
    reportVeracityEvent("suppress_synthetic", opts.surface, null, ctx.classification);
    return { text: null, gate: "suppress_synthetic" };
  }
  // 2. Recovery — a queued-for-alert utterance whose row recovered speaks
  //    the recovery sentence, never the original alert sentence.
  if (opts.queuedForAlert && ctx.alert_state === "recovered") {
    reportVeracityEvent("rewrite_recovery", opts.surface, null, ctx.classification);
    return { text: recoverySentence(ctx), gate: "rewrite_recovery" };
  }
  // 3. Staleness — never speak a stale value as current.
  if (ctx.value_mgdl == null) {
    return {
      text: opts.staleMode === "speak" ? noDataSentence(ctx) : null,
      gate: "silent_no_data",
    };
  }
  if (ctx.stale) {
    if (opts.staleMode === "speak") {
      reportVeracityEvent("rewrite_stale", opts.surface, null, ctx.classification);
      return { text: staleSentence(ctx), gate: "rewrite_stale" };
    }
    return { text: null, gate: "silent_stale" };
  }
  // 4. Consistency tripwire — the template's implied classification must
  //    match the context's. templateFor derives the template FROM the
  //    context so this cannot fire today; it exists to catch future
  //    drift (a hand-built template, a refactor) — the class of bug that
  //    produced "speaks high when it is not".
  const { text, implied } = templateFor(ctx);
  if (implied != null && ctx.classification != null && implied !== ctx.classification) {
    reportVeracityEvent("abort", opts.surface, implied, ctx.classification);
    return { text: null, gate: "abort_inconsistent" };
  }
  return { text, gate: "spoken" };
}

/** Exported for tests + any future hand-built template: run ONLY the
 *  consistency tripwire against a context. */
export function veracityConsistencyCheck(
  impliedClassification: UtteranceClassification | null,
  ctx: GlucoseUtteranceContext,
  surface: string,
): boolean {
  if (
    impliedClassification != null &&
    ctx.classification != null &&
    impliedClassification !== ctx.classification
  ) {
    reportVeracityEvent("abort", surface, impliedClassification, ctx.classification);
    return false;
  }
  return true;
}
